import React from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { onActivateKeyDown } from "../../../utils/keyboardActivate";
import {
  StyledDialog,
  DialogHeader,
  HeaderTitle,
  Title,
  CloseButton,
  Separator,
  ScrollableContent,
  ButtonsContainer,
  CancelText,
} from "../SubscriptionModal.styled";
import Button from "../../Button";
import TextView from "../../TextView";
import { colors, tokens } from "../../../utils";
import { MOVE_PLAN_STATES } from "../../Settings/Subscription/movePlanState";

/**
 * ISP-9999 (Option B4, fe-subB4-plan-move-flow): "Move my plan" -- lets a
 * store subscriber (apple_iap/google_play) start a web (Stripe) plan without
 * touching their store subscription. The store subscription is left running
 * until its own period end; the new Stripe subscription is anchored to start
 * billing right after (server-side, subscription.move_plan.MovePlanView), so
 * the panel here never asks the user to pick a start date -- only to confirm.
 *
 * Every state the panel can be in is a real, designed render branch (no
 * blank/undefined fallthrough):
 * - NO_ENTITLEMENT: flag on, store source, but no live Entitlement row yet.
 * - READY: the normal confirm-and-checkout state.
 * - ALREADY_WEB: nothing to move (defensive -- the panel's callers should
 *   not open it in this state, but a disconnected caller must not blank-render).
 * - error (checkoutError set): the move-plan/ request failed.
 */
const MovePlanModal = ({
  open,
  onClose,
  state,
  toPlanName,
  periodEndDisplay,
  storeLabel,
  storeManageHref,
  loading = false,
  checkoutError = null,
  onConfirm,
}) => {
  const renderBody = () => {
    if (checkoutError) {
      return (
        <TextView
          variant="text"
          data-testid="move-plan-error"
          style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
        >
          <ErrorOutlineIcon
            style={{ color: colors.ErrorRed ?? "#D32F2F", fontSize: 20, flexShrink: 0 }}
          />
          <span>{checkoutError}</span>
        </TextView>
      );
    }

    if (state === MOVE_PLAN_STATES.NO_ENTITLEMENT) {
      return (
        <TextView
          variant="text"
          data-testid="move-plan-no-entitlement"
          style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
        >
          <InfoOutlinedIcon
            style={{ color: colors.PrimaryPurple700, fontSize: 20, flexShrink: 0 }}
          />
          <span>
            We couldn&apos;t find your {storeLabel} subscription details yet.
            Please try again shortly, or manage your subscription directly in
            the store.
          </span>
        </TextView>
      );
    }

    if (state === MOVE_PLAN_STATES.ALREADY_WEB) {
      return (
        <TextView variant="text" data-testid="move-plan-already-web">
          You&apos;re already on a web plan -- there&apos;s nothing to move.
        </TextView>
      );
    }

    // READY
    return (
      <>
        <TextView variant="text" data-testid="move-plan-ready-copy">
          Your {storeLabel} subscription keeps running as-is until{" "}
          <strong>{periodEndDisplay}</strong> -- it will not be changed or
          cancelled by this. Starting today, we&apos;ll set up{" "}
          <strong>{toPlanName}</strong> on the web, billed from{" "}
          <strong>{periodEndDisplay}</strong> so you&apos;re never charged
          twice for the same period.
        </TextView>
        {storeManageHref && (
          <TextView
            variant="text"
            style={{ marginTop: 12, color: "#666" }}
            data-testid="move-plan-store-cancel-instructions"
          >
            To stop your {storeLabel} subscription instead, manage it from{" "}
            <a
              href={storeManageHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.PrimaryPurple700 }}
            >
              {storeLabel}
            </a>
            .
          </TextView>
        )}
      </>
    );
  };

  const showConfirm = state === MOVE_PLAN_STATES.READY;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={false}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogHeader>
        <HeaderTitle>
          <SyncAltOutlinedIcon style={{ color: colors.PrimaryPurple700 }} />
          <Title>Move my plan</Title>
        </HeaderTitle>
        <CloseButton
          role="button"
          tabIndex={0}
          aria-label="Close"
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <Separator />

      <ScrollableContent style={{ padding: "24px 32px" }}>
        {renderBody()}
      </ScrollableContent>

      <ButtonsContainer>
        <CancelText
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          {showConfirm ? "Cancel" : "Close"}
        </CancelText>
        {showConfirm && (
          <Button
            width="auto"
            style={{
              minWidth: 160,
              backgroundColor: loading ? undefined : tokens.brand.primaryActive,
            }}
            onClick={onConfirm}
            disabled={loading}
            data-testid="move-plan-confirm"
          >
            {loading ? (
              <CircularProgress size={20} style={{ color: "white" }} />
            ) : (
              "Start on Web"
            )}
          </Button>
        )}
      </ButtonsContainer>
    </StyledDialog>
  );
};

MovePlanModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  state: PropTypes.oneOf(Object.values(MOVE_PLAN_STATES)).isRequired,
  toPlanName: PropTypes.string,
  periodEndDisplay: PropTypes.string,
  storeLabel: PropTypes.string,
  storeManageHref: PropTypes.string,
  loading: PropTypes.bool,
  checkoutError: PropTypes.string,
};

export default MovePlanModal;
